import { determinedSimpleProduct } from '../helpers/basket-items'
import _ from 'lodash'
import axios from 'axios';
import resetStore from '../helpers/reset-store'

export const state = () => ({
  items: [],
  employees: [],
  selectedEmployee: null,
  contactBudgets: [],
  currentBudgets: {},
  itemsLoading: false
})

export const mutations = {
  reset: resetStore(state),
  setItems(state, items) {
    state.items = items
  },

  setEmployees(state, employees) {
    state.employees = employees
  },

  setEmployee(state, employee) {
    state.selectedEmployee = employee
  },
  mergeEmployees(state, employees) {
    // Merge new employees with existing ones, avoiding duplicates
    const existingEmployeeIds = state.employees.data ? state.employees.data.map(emp => emp.internalId) : []
    const newEmployees = employees.data ? employees.data.filter(emp => !existingEmployeeIds.includes(emp.internalId)) : []
    
    if (state.employees.data) {
      state.employees.data = [...state.employees.data, ...newEmployees]
    } else {
      state.employees = employees
    }
    
    // Update the total count
    if (state.employees.total && employees.total) {
      state.employees.total = Math.max(state.employees.total, employees.total)
    }
  },
  setContactBudgets(state, value) {
    state.contactBudgets = value
  },
  setCurrentBudgets(state, value) {
    state.currentBudgets = value
  },
  setItemsLoading(state, loading) {
    state.itemsLoading = loading
  }
}

export const getters = {
  items(state) {
    return state.items
  },
  currentBudgets(state) {
    return state.currentBudgets
  },
  employees(state) {
    return state.employees
  },

  selectedEmployee(state) {
    return state.selectedEmployee
  },
  contactBudgets(state) {
    return state.contactBudgets
  },
  budgetType(state) {
    return _.get(
      state.selectedEmployee,
      'custentity_cbc_contact_budget_type.value.$attributes.internalId'
    )
  },
  selectedGrade(state) {
    return _.get(
      state,
      'selectedEmployee.custentity_cbc_contact_grade.value.$attributes.internalId',
      0
    )
  },
  itemsLoading(state) {
    return state.itemsLoading
  }
}

export const actions = {
  async getContactBudgets({ commit }, { ids }) {
    let data = await axios.get('/api/contactBudget', {
      params: {
        'custrecord_cbc_contact_id.value.$attributes.internalId[$in]': ids,
        $limit: 10000
      }
    })

    data = data.data.data.map(o => {
      return {
        contactId: o.custrecord_cbc_contact_id.value.$attributes.internalId,
        type: o.custrecord_cbc_contact_budget_type.value.$attributes.internalId,
        qty: parseFloat(_.get(o, 'custrecord_cbc_contact_quantity.value', 0)),
        resetDays: parseInt(
          _.get(o, 'custrecord_cbc_contact_reset_days.value', 0)
        ),
        resetDate: _.get(o, 'custrecord_cbc_contact_reset_date.value'),
        allocationType: _.get(
          o,
          'custrecord_cbc_contact_item_alloc_type.value.$attributes.internalId'
        ),
        allocationTypeName: _.get(
          o,
          'custrecord_cbc_contact_item_alloc_type.value.name'
        ),
        usedQty: +_.get(o, 'custrecord_cbc_contact_usage.value', 0)
      }
    })

    commit('setContactBudgets', data)
  },
  async fetchItems({ commit, state }, params) {
    commit('setItemsLoading', true)
    commit('setItems', []) // Clear existing items
    
    try {
      params = {
        ...params,
        scope: 'manpack',
        includeFinished: true,
        includeChildren: true,
        includeAllocation: true,
        includePricing: true,
        includeBudget: true,
        contactId: _.get(state, 'selectedEmployee.internalId'),
        grade: _.get(
          state,
          'selectedEmployee.custentity_cbc_contact_grade.value.$attributes.internalId',
          0
        )
      }
      const { data } = await axios.get('/api/item', { params: params })

      commit('setItems', data)
    } finally {
      commit('setItemsLoading', false)
    }
  },

  async fetchEmployee({ commit }, params) {
    const { data } = await axios.get('/api/contact', { params: params })
    commit('setEmployees', data)
  },

  async fetchAndMergeEmployee({ commit }, params) {
    const { data } = await axios.get('/api/contact', { params: params })
    commit('mergeEmployees', data)
  },

  async fetchEmployeesWithFiltering({ commit, rootGetters }, { currentContactOnly = false, currentUserId = null } = {}) {
    const contactParams = {
      isInactive: false,
      manpackUser: true,
      $limit: 10000
    }

    // Add the currentContactOnly parameter to restrict to current user if needed
    if (currentContactOnly && currentUserId) {
      contactParams.currentContactOnly = true
    }

    const { data } = await axios.get('/api/contact', { params: contactParams })
    commit('setEmployees', data)
  },

  async addItemsToBasket({ dispatch }, { employee, data, isAdd = true }) {
    if (
      employee !== '' &&
      {}.toString.call(data) === '[object Array]' &&
      data.length
    ) {
      // TODO: Convert quote item data from item
      // Add quoteItem data into local storage
      data.map(item => {
        dispatch('addItemToBasket', {
          employee: employee,
          product: item,
          isAdd: isAdd
        })
      })
    }
  },

  async addItemToBasket({ commit }, { employee, product, isAdd = true }) {
    if (employee !== '' && product.internalId) {
      // TODO: Convert quote item data from item
      // Add quoteItem data into Basket State
      if (
        product.matrixType === '_parent' ||
        product.custitem_bbs_item_finish_ref
      ) {
        // Determined the product simple by attribute option that user selected
        const simple = determinedSimpleProduct(product)

        if (simple) {
          commit(
            'basket/addManpackBasketItem',
            {
              employee: employee,
              item: {
                removeId: product.internalId,
                parentId: product.internalId,
                productId: simple.internalId,
                qty: product.qty,
                options: product.options || {}
              },
              isAdd: isAdd
            },
            { root: true }
          )
        }
      } else {
        commit(
          'basket/addManpackBasketItem',
          {
            employee: employee,
            item: {
              removeId: product.internalId,
              parentId: product.internalId,
              productId: product.internalId,
              qty: product.qty,
              editing: product.editing || false
            },
            isAdd: isAdd
          },
          { root: true }
        )
      }
    }
  }
}

export default {
  namespaced: true, // optional, if you want namespacing
  state,
  mutations,
  actions,
  getters,
};